// src/components/PhotoCarousel.js
import {
  Box,
  Image,
  Text,
  VStack,
  Button,
  Center,
  Skeleton,
} from "@chakra-ui/react";
import Slider from "react-slick";
import { fetchPhotos } from "../api/api";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Local images & video
import image1 from "../images/image1.jpeg";
import image2 from "../images/image2.png";
import image3 from "../images/image3.jpeg";
import image4 from "../images/image4.jpeg";
import image5 from "../images/image5.jpeg";
import image7 from "../images/image7.JPG";
import image8 from "../images/image8.jpeg";
import image9 from "../images/image9.jpeg";
import video1 from "../videos/construction.mov";

const SAFFRON = "#E07B1A";
const SAFFRON_DARK = "#854F0B";

export default function PhotoCarousel() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const localMedia = [
      { id: "local-8", url: image9, filename: "image9.jpeg", type: "image" },
      { id: "local-7", url: image8, filename: "image8.jpeg", type: "image" },
      {
        id: "local-vid1",
        url: video1,
        filename: "construction.mov",
        type: "video",
      },
      { id: "local-6", url: image7, filename: "image7.JPG", type: "image" },
      { id: "local-1", url: image1, filename: "image1.jpeg", type: "image" },
      { id: "local-2", url: image2, filename: "image2.png", type: "image" },
      { id: "local-3", url: image3, filename: "image3.jpeg", type: "image" },
      { id: "local-4", url: image4, filename: "image4.jpeg", type: "image" },
      { id: "local-5", url: image5, filename: "image5.jpeg", type: "image" },
    ];

    fetchPhotos()
      .then((res) => {
        const apiMedia = (res?.data || []).map((item, idx) => ({
          id: item.id ?? `api-${idx}`,
          url: item.url,
          filename: item.filename ?? item.url,
          type: item.type ?? "image",
          ...item,
        }));
        setPhotos([...localMedia, ...apiMedia]);
      })
      .catch(() => setPhotos(localMedia))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <Center py={6}>
        <Skeleton
          height={{ base: "280px", md: "440px" }}
          width="100%"
          maxW="900px"
          borderRadius="2xl"
          startColor="orange.100"
          endColor="orange.50"
        />
      </Center>
    );

  if (!photos.length)
    return (
      <Center py={10}>
        <Text color="gray.500">No construction photos yet.</Text>
      </Center>
    );

  const settings = {
    dots: true,
    infinite: true,
    speed: 700,
    fade: true,
    cssEase: "cubic-bezier(0.45,0,0.15,1)",
    slidesToShow: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    beforeChange: (_, next) => {
      const nextItem = photos[next];
      if (nextItem?.type === "video") {
        if (sliderRef.current && sliderRef.current.slickPause)
          sliderRef.current.slickPause();
      }
    },
    accessibility: true,
    adaptiveHeight: false,
  };

  const handleVideoEnd = () => {
    if (sliderRef.current) {
      if (sliderRef.current.slickPlay) sliderRef.current.slickPlay();
      if (sliderRef.current.slickNext) sliderRef.current.slickNext();
    }
  };

  return (
    <Box maxW="900px" mx="auto" px={{ base: 3, md: 0 }}>
      {/* Custom slick dot styling — saffron themed */}
      <style>{`
        .temple-carousel .slick-dots {
          bottom: 14px;
        }
        .temple-carousel .slick-dots li button:before {
          font-size: 8px;
          color: white;
          opacity: 0.55;
          text-shadow: 0 1px 3px rgba(0,0,0,0.4);
        }
        .temple-carousel .slick-dots li.slick-active button:before {
          opacity: 1;
          color: ${SAFFRON};
          font-size: 10px;
        }
        .temple-carousel .slick-prev,
        .temple-carousel .slick-next {
          z-index: 2;
          width: 38px;
          height: 38px;
        }
        .temple-carousel .slick-prev { left: 12px; }
        .temple-carousel .slick-next { right: 12px; }
        .temple-carousel .slick-prev:before,
        .temple-carousel .slick-next:before {
          font-size: 22px;
          opacity: 0.9;
          text-shadow: 0 1px 4px rgba(0,0,0,0.5);
        }
        .temple-carousel .slick-slide > div {
          height: 100%;
        }
      `}</style>

      <Box
        className="temple-carousel"
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="0 10px 32px rgba(133,79,11,0.22)"
        position="relative"
      >
        <Slider ref={sliderRef} {...settings}>
          {photos.map((p) => (
            <Box key={p.id} position="relative">
              {p.type === "video" ? (
                <video
                  src={p.url}
                  muted
                  autoPlay
                  playsInline
                  onEnded={handleVideoEnd}
                  aria-label={p.filename || "construction video"}
                  style={{
                    width: "100%",
                    height: "clamp(280px, 45vw, 440px)",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              ) : (
                <Image
                  src={p.url}
                  alt={p.filename || "construction photo"}
                  width="100%"
                  height="clamp(280px, 45vw, 440px)"
                  objectFit="cover"
                  display="block"
                />
              )}

              {/* Soft bottom gradient so dots/arrows stay legible over any image */}
              <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                height="90px"
                bgGradient="linear(to-t, blackAlpha.500, transparent)"
                pointerEvents="none"
              />
            </Box>
          ))}
        </Slider>
      </Box>

      {/* Telugu intro & Donate */}
      <VStack spacing={4} mt={8} textAlign="center">
        <Box w="36px" h="2px" bg={SAFFRON} borderRadius="full" opacity={0.6} />

        <Text
          fontSize={{ base: "md", md: "lg" }}
          fontWeight="700"
          color={SAFFRON_DARK}
          lineHeight="1.7"
          maxW="640px"
        >
          రాజమహేంద్రవరం సమీపంలోని తోకాడ గ్రామంలో నిర్మాణంలో ఉన్న పవిత్రమైన
          దేవాలయం.
        </Text>
        <Text
          fontSize={{ base: "sm", md: "md" }}
          color="gray.600"
          lineHeight="1.8"
          maxW="600px"
        >
          భవిష్యత్ తరాల కోసం ఈ ఆధ్యాత్మిక ఆలయ నిర్మాణంలో మీరు కూడా భాగస్వాములు
          కావాలి.
        </Text>

        <Button
          size="lg"
          bg={SAFFRON}
          color="white"
          fontWeight="700"
          px={8}
          borderRadius="full"
          boxShadow="0 6px 18px rgba(224,123,26,0.35)"
          _hover={{
            bg: SAFFRON_DARK,
            transform: "translateY(-2px)",
            boxShadow: "0 8px 22px rgba(133,79,11,0.4)",
          }}
          _active={{ transform: "translateY(0)" }}
          transition="all 0.18s ease"
          onClick={() => navigate("/donate")}
          aria-label="Donate now"
        >
          🙏 Donate Now
        </Button>
      </VStack>
    </Box>
  );
}
